import React from 'react';
import { BrazierType, BrazierIntensity } from './types';
import { useAudio } from '../../audio/useAudio';

interface BraziersProps {
  intensities: Record<BrazierType, BrazierIntensity>;
  onToggleBrazier: (type: BrazierType) => void;
}

const BRAZIER_CONFIGS: Record<BrazierType, {
  name: string;
  metal: string;
  element: string;
  symbol: string;
  colorName: string;
  imageSrc: string;
  glowColor: string;
  shadow: string;
  description: string;
}> = {
  iron: {
    name: 'Brazier of Iron',
    metal: 'Forged Pig Iron',
    element: 'Chthonic Fire / Mars',
    symbol: 'Fe',
    colorName: 'text-ember-flare',
    imageSrc: '/assets/forge/brazier_iron.png',
    glowColor: 'rgba(255, 72, 20, 0.45)',
    shadow: 'shadow-glow-ember',
    description: 'Unbending Will, Kinetic Aggression, Severing Attachments'
  },
  bronze: {
    name: 'Brazier of Bronze',
    metal: 'Cast Antique Bronze',
    element: 'Solar-Earth / Sovereign Crown',
    symbol: 'Cu',
    colorName: 'text-gold-bright',
    imageSrc: '/assets/forge/brazier_bronze.png',
    glowColor: 'rgba(230, 197, 110, 0.45)',
    shadow: 'shadow-glow-gold',
    description: 'Material Abundance, Pacts of Parity, Temporal Dominion'
  },
  basalt: {
    name: 'Brazier of Basalt',
    metal: 'Volcanic Black Basalt',
    element: 'Saturnian Bedrock / Sheol',
    symbol: 'Ba',
    colorName: 'text-iron-steel',
    imageSrc: '/assets/forge/brazier_basalt.png',
    glowColor: 'rgba(168, 85, 247, 0.45)',
    shadow: 'shadow-chthonic',
    description: 'Absolute Demurrer, Unshakeable Mass, Dissolution of Guilt'
  }
};

export const Braziers: React.FC<BraziersProps> = ({ intensities, onToggleBrazier }) => {
  const { playFlameIgnite } = useAudio();

  const handleBrazierClick = (type: BrazierType) => {
    playFlameIgnite();
    onToggleBrazier(type);
  };

  // Determine synergy
  const ironActive = intensities.iron !== 'extinguished';
  const bronzeActive = intensities.bronze !== 'extinguished';
  const basaltActive = intensities.basalt !== 'extinguished';
  const activeCount = (ironActive ? 1 : 0) + (bronzeActive ? 1 : 0) + (basaltActive ? 1 : 0);

  let synergyTitle = 'Silent Altar';
  let synergyDesc = 'Ignite the three braziers to awaken the full crucible of the Unyoked.';

  if (activeCount === 3) {
    synergyTitle = 'THE CRUCIBLE OF THE UNYOKED (Full Synergy)';
    synergyDesc = 'All three mineral currents unlocked. Kinetic hammer strikes inflict maximum severance upon inscribed bondages.';
  } else if (ironActive && bronzeActive) {
    synergyTitle = 'THE FORGE OF CONQUEST';
    synergyDesc = 'Marriage of iron severance and bronze wealth. Awakens aggressive temporal manifestation.';
  } else if (ironActive && basaltActive) {
    synergyTitle = 'THE TOMB OF THE YOKE';
    synergyDesc = 'Deep bedrock density and severing blade. Irreversible expungement of generational debts.';
  } else if (bronzeActive && basaltActive) {
    synergyTitle = 'THE VAULT OF THE EARTH KING';
    synergyDesc = 'Sovereign material stability and indestructible boundary defense.';
  } else if (activeCount > 0) {
    synergyTitle = 'Single Flame Awakened';
    synergyDesc = 'Continue kindling the remaining braziers to achieve alchemical equilibrium.';
  }

  return (
    <div className="space-y-6" id="braziers-component">
      {/* Synergy Status Banner */}
      <div className="rounded-lg border border-bronze-dark/60 bg-obsidian-deep/90 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-chthonic relative overflow-hidden">
        <div className="relative z-10">
          <span className="text-[10px] font-mono text-gold-ancient uppercase tracking-widest block">
            ALTAR ALCHEMICAL SYNERGY
          </span>
          <h4 className="font-display text-base sm:text-lg font-bold text-gold-bright">
            {synergyTitle}
          </h4>
          <p className="text-xs text-parchment-dim font-serif italic mt-0.5">
            {synergyDesc}
          </p>
        </div>
        <div className="flex items-center gap-2 relative z-10">
          {(['iron', 'bronze', 'basalt'] as BrazierType[]).map((b) => (
            <span
              key={b}
              className={`inline-block px-2.5 py-1 text-[10px] font-mono rounded uppercase border ${
                intensities[b] === 'blazing'
                  ? 'border-ember-flare bg-ember-dark/40 text-ember-spark shadow-glow-ember'
                  : intensities[b] === 'kindled'
                  ? 'border-gold-bright/60 bg-gold-ancient/20 text-gold-bright'
                  : 'border-iron-dark bg-obsidian-surface text-iron-steel'
              }`}
            >
              {b}: {intensities[b]}
            </span>
          ))}
        </div>
      </div>

      {/* The 3 Braziers Interactive Pods */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {(['iron', 'bronze', 'basalt'] as BrazierType[]).map((type) => {
          const config = BRAZIER_CONFIGS[type];
          const intensity = intensities[type];
          const isLit = intensity !== 'extinguished';
          const isBlazing = intensity === 'blazing';

          return (
            <div
              key={type}
              onClick={() => handleBrazierClick(type)}
              role="button"
              tabIndex={0}
              aria-label={`${config.name} (${intensity}). Click to cycle flame.`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleBrazierClick(type);
                }
              }}
              className={`group cursor-pointer rounded-lg border-2 p-6 flex flex-col items-center justify-between text-center transition-all duration-500 select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-bright relative overflow-hidden ${
                isBlazing
                  ? `border-ember-flare bg-gradient-to-b from-obsidian-surface to-[#1A1210] ${config.shadow} scale-[1.02]`
                  : isLit
                  ? 'border-gold-ancient bg-gradient-to-b from-obsidian-surface to-obsidian-deep shadow-glow-gold'
                  : 'border-iron-dark bg-obsidian-deep/90 hover:border-iron-base'
              }`}
            >
              {/* Chthonic Atmosphere Glow */}
              {isLit && (
                <div
                  className={`absolute -top-12 inset-x-0 h-40 rounded-full blur-3xl pointer-events-none transition-opacity duration-700 ${
                    isBlazing ? 'opacity-50' : 'opacity-25'
                  }`}
                  style={{ backgroundColor: config.glowColor }}
                />
              )}

              {/* Photorealistic Brazier Shrine Artwork */}
              <div className="relative w-48 h-48 sm:w-52 sm:h-52 flex items-center justify-center mb-4 transition-transform duration-500">
                <img
                  src={config.imageSrc}
                  alt={config.name}
                  className={`w-full h-full object-contain transition-all duration-700 pointer-events-none drop-shadow-2xl ${
                    isBlazing
                      ? 'brightness-125 contrast-110 scale-105 filter drop-shadow-[0_0_30px_rgba(255,120,40,0.6)] animate-pulse'
                      : isLit
                      ? 'brightness-105 filter drop-shadow-[0_0_18px_rgba(230,197,110,0.35)]'
                      : 'opacity-40 grayscale-[0.6] brightness-70 contrast-125'
                  }`}
                />

                {/* Hot White Flicker Core during Blazing state */}
                {isBlazing && (
                  <div className="absolute top-10 inset-x-12 h-14 bg-gradient-to-t from-transparent via-white/30 to-amber-200/40 rounded-full blur-md pointer-events-none animate-ping opacity-75" />
                )}

                {/* Subdued Status Badge Overlay */}
                <div className="absolute bottom-1 right-2">
                  <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-black/80 border border-iron-dark text-iron-steel font-bold">
                    {config.symbol}
                  </span>
                </div>
              </div>

              {/* Title & Metadata */}
              <div className="space-y-1.5 w-full border-t border-iron-dark/60 pt-4 relative z-10">
                <span className="text-[10px] font-mono text-gold-ancient uppercase tracking-widest block">
                  {config.metal}
                </span>
                <h4 className="font-display text-lg font-bold text-parchment-light group-hover:text-gold-bright transition-colors">
                  {config.name}
                </h4>
                <div className="text-[11px] font-serif text-parchment-dim italic">
                  {config.element}
                </div>
                <p className="text-[11px] text-parchment-dim/80 pt-1 leading-snug font-serif">
                  {config.description}
                </p>
              </div>

              {/* Action Hint */}
              <div className="pt-3 w-full relative z-10">
                <span className={`block w-full py-1.5 rounded text-[10px] font-mono uppercase tracking-wider transition-colors border ${
                  isBlazing
                    ? 'bg-ember-dark/40 border-ember-flare text-ember-spark font-bold'
                    : isLit
                    ? 'bg-gold-ancient/20 border-gold-bright/60 text-gold-bright font-bold'
                    : 'bg-obsidian-deep border-iron-dark text-parchment-dim hover:text-parchment-light'
                }`}>
                  STATE: {intensity.toUpperCase()} • CLICK TO CYCLE
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Braziers;
